/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router';

import ContentFrame from '../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareAppNavStep } from '../../../components/ScrollAware';

import ProfileName from './ProfileName';
import ProfileDateOfBirth from './ProfileDateOfBirth';
import ProfileGender from './ProfileGender.redux';
import ProfileResidence from './ProfileResidence';
import HealthInfoPrivacy, { HealthInfoPrivacyClass } from './HealthInfoPrivacy';
import ViewGroupConsent, { ViewGroupConsentClass } from './ViewGroupConsent';
import ProfilePrivacyConsent, { ProfilePrivacyConsentClass } from './ProfilePrivacyConsent';
import PolicyDependentAuthorization, { PolicyDependentAuthorizationClass } from './PolicyDependentAuthorization/PolicyDependentAuthorization';

import UserModel from '../../../../models/UserModel';
import ProfileModel from '../../../../models/ProfileModel';
import GenderModel from '../../../../models/GenderModel';
import { ActionItemViewName } from '../../../../models/ActionItemModel';
import AppUtils from '../../../../utilities/AppUtils';
import ProfileActions from '../../redux/ProfileActions';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import ProfileService from '../../../../apiServices/ProfileService';

import { ProfileSetupIntro } from './ProfileSetupIntro';
import DoNotConsentModal, { WithdrawName } from '../../../components/modal/DoNotConsentModal';
import { PolicyName } from '../../../../models/PolicyModel';

class ProfileSetupController extends React.Component {

  constructor(props) {
    super(props);

    this.decrementStep = this.decrementStep.bind(this);
    this.submitProfileChanges = this.submitProfileChanges.bind(this);
    this.onChangeProfile = this.onChangeProfile.bind(this);
    this.stepIsValid = this.stepIsValid.bind(this);
    this.consentToPoliciesOnView = this.consentToPoliciesOnView.bind(this);

    const isMainProfile = props.isMainProfile;
    const profile = new ProfileModel(props.profile.data);

    this.state = {
      profile,
      prevProfile: new ProfileModel(props.profile.data),
      profileIsLoaded: !!profile.data.id,
      defaultOtherGenderText: props.intl.formatMessage({ id: 'list.gender.other.name' }),
      submitErrorText: '',
      errorMessage: '',
      currentStep: 0,
      actionItems: [],
      isFinished: false,
      loading: false,
      agreedToPolicyPersonalInfo: false,
      agreedToPolicyHealthInfo: false,
      agreedToPolicyDependentAuth: false,
      agreedToGroupConsent: false,
      fieldErrorName: '',
      steps: [
        {
          name: 'intro',
          submitDisabled: false,
          cancel: () => {
            props.clearIntent(props.user.data, () => {
              if (this.props.isNewProfile || this.props.isMainProfile) {
                AppUtils.logout({}, AppUtils.reloadApp);
              } else {
                this.props.switchToMainProfile();
                this.props.goToHome();
              }
            });
          },
          next: this.nextStep,
          onDisabledClick: null,
          isValid: [],
          viewErrorMessage: [],
          fieldErrorName: [''],
          content: ProfileSetupIntro,
          phraseData: this.props.isMainProfile ? ProfilePhrases.profileSetupIntro(this.isFreshProfile()) : ProfilePhrases.profileSetupIntro(this.isFreshProfile()),
          title: () => (this.props.isMainProfile ? ProfilePhrases.profileSetupIntro(this.isFreshProfile()).title : ProfilePhrases.dependentProfileSetupIntro(this.isFreshProfile(), this.props.profile.greetingName()).title),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(null); },
        },
        {
          name: ActionItemViewName.policy_dependent_authorization,
          submitDisabled: true,
          goBack: this.decrementStep,
          next: () => {
            this.consentToPoliciesOnView().then(() => {
              this.setState({ agreedToPolicyDependentAuth: true }, () => {
                // intro counts as a step, so need to subtract 1
                if (this.state.currentStep >= (this.state.steps.length - 1)) {
                  this.exitWizard();
                } else {
                  this.nextStep();
                }
              });
            });
          },
          onDisabledClick: () => {
            PolicyDependentAuthorizationClass.scrollAcceptButtonIntoView();
          },
          isValid: [() => this.state.agreedToPolicyDependentAuth],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: (contentProps) => <PolicyDependentAuthorization {...contentProps} allowManageAccess={true} />,
          phraseData: ProfilePhrases.policyDependentAuth(isMainProfile),
          title: () => ProfilePhrases.policyDependentAuth(this.props.isMainProfile).title,
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.DEPENDENT_AUTHORIZATION); },
        },
        {
          name: ActionItemViewName.policy_personal_info,
          submitDisabled: false,
          goBack: this.decrementStep,
          next: () => {
            this.consentToPoliciesOnView();
            this.setState({ agreedToPolicyPersonalInfo: true }, () => {
              if (this.state.currentStep >= (this.state.steps.length - 1)) {
                this.exitWizard();
              } else {
                this.nextStep();
              }
            });
          },
          onDisabledClick: () => {
            ProfilePrivacyConsentClass.scrollAcceptButtonIntoView();
          },
          isValid: [() => this.state.agreedToPolicyPersonalInfo],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfilePrivacyConsent,
          phraseData: ProfilePhrases.profilePrivacyConsent(isMainProfile),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.PRIVACY); },
        },
        {
          name: ActionItemViewName.profile_name,
          submitDisabled: false,
          goBack: () => {
            this.setState({
              currentStep: this.state.currentStep - 1,
            });
          },
          onDisabledClick: this.checkValidation,
          isValid: [() => this.state.profile.hasGivenOrFamilyName(), () => this.state.profile.hasPreferredName()],
          viewErrorMessage: ['error.name-validator.missing-given-family-names.message', 'error.name-validator.missing-preferred-name.message'],
          fieldErrorName: ['', ''],
          content: ProfileName,
          phraseData: ProfilePhrases.profileName(isMainProfile),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.NAME); },
        },
        {
          name: ActionItemViewName.profile_residence,
          submitDisabled: false,
          goBack: () => {
            this.setState({
              currentStep: this.state.currentStep - 1,
            });
          },
          onDisabledClick: null,
          isValid: [() => {
            if (!this.state.profile.hasValidResidence()) {
              return false;
            } else if (this.props.isResidenceStateRequired(this.state.profile.residenceCountryId)) {
              return !!this.state.profile.residenceStateId;
            }

            return true;
          }],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileResidence,
          phraseData: ProfilePhrases.profileResidence(isMainProfile, profile.greetingName()),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.RESIDENCE); },
        },
        {
          name: ActionItemViewName.profile_dob,
          submitDisabled: false,
          goBack: () => {
            this.setState({
              profile: new ProfileModel(this.state.prevProfile.data),
              currentStep: this.state.currentStep - 1,
            });
          },
          onDisabledClick: null,
          isValid: [() => this.state.profile.hasValidDateOfBirth()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileDateOfBirth,
          phraseData: ProfilePhrases.profileDateOfBirth(isMainProfile),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.DATE_OF_BIRTH); },
        },
        {
          name: ActionItemViewName.profile_gender,
          goBack: this.decrementStep,
          onDisabledClick: null,
          isValid: [() => this.state.profile.hasValidGender(), () => this.state.profile.hasValidBirthSex()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileGender,
          phraseData: ProfilePhrases.profileGender(isMainProfile),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.GENDER); },
        },
        {
          name: ActionItemViewName.policy_health_info,
          submitDisabled: false,
          goBack: this.decrementStep,
          next: () => {
            this.setState({ agreedToPolicyHealthInfo: true }, this.submitProfileChanges);
          },
          onDisabledClick: () => {
            HealthInfoPrivacyClass.scrollAcceptButtonIntoView();
          },
          isValid: [() => this.state.agreedToPolicyHealthInfo],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: HealthInfoPrivacy,
          phraseData: ProfilePhrases.healthInfo(isMainProfile),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.HEALTH_INFO); },
        },
        {
          name: ActionItemViewName.policy_group_info,
          submitDisabled: false,
          goBack: this.decrementStep,
          next: () => {
            this.setState({ agreedToGroupConsent: true }, () => {
              // this.consentToGroupPolicies().then(() => {
                if (this.state.currentStep >= (this.state.steps.length - 1)) {
                  this.submitProfileChanges();
                } else {
                  this.nextStep();
                }
              // })
            });
          },
          onDisabledClick: () => {
            ViewGroupConsentClass.scrollAcceptButtonIntoView();
          },
          isValid: [() => this.state.agreedToGroupConsent],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ViewGroupConsent,
          phraseData: ProfilePhrases.groupConsent(isMainProfile),
          onWithdrawConsent: () => { this.handleOnWithdrawPolicyConsent(PolicyName.GROUP_CONSENT); },
        },
      ],
    };
  }

  UNSAFE_componentWillMount() {
    const { profileActionItems } = this.props;
    const { steps } = this.state;
    const actionItemViews = profileActionItems.map(p => p.view);

    if (!profileActionItems.length) {
      this.exitWizard();
      return;
    }

    // filter out completed steps
    for (let i = 1; i < steps.length; i++) {
      if (steps[i].name && actionItemViews.indexOf(steps[i].name) < 0) {
        steps.splice(i, 1);
        i--;
      }
    }

    this.setState({ actionItems: profileActionItems, steps });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.state.profileIsLoaded) {
      const profile = new ProfileModel(nextProps.profile.data);
      this.setState({
        profile,
        prevProfile: new ProfileModel(nextProps.profile.data),
        profileIsLoaded: !!profile.data.id,
      });
    }
  }

  onChangeProfile(profileData, callback) {
    this.setState({
      profile: new ProfileModel(profileData),
      fieldErrorName: '',
    }, callback);
  }

  clearFieldErrors = () => {
    this.setState({ fieldErrorName: '' });
  }

  consentToGroupPolicies(e) {
    if (e) { e.preventDefault(); }
    const { profileId, groupIds } = this.props;
    const currentStep = this.state.steps[this.state.currentStep];
    const actionItem = this.state.actionItems.filter(a => a.view === currentStep.name)[0];
    const policyIds = actionItem.policy_id;

    if (groupIds.length === 0) { return; }

    const promises = groupIds.map(id => ProfileService.provideGroupConsent(profileId, id, policyIds));

    return Promise.all(promises);
  }

  consentToPoliciesOnView() {
    const { profile } = this.props;

    const currentStep = this.state.steps[this.state.currentStep];
    const actionItem = this.state.actionItems.filter(a => a.view === currentStep.name)[0];
    if (!actionItem) { return; }

    const policyList = actionItem.policy_id;
    const isGroupConsent = actionItem.view === ActionItemViewName.policy_group_info;
    if (policyList.length === 0) { return; }

    if (isGroupConsent) {
      return this.consentToGroupPolicies();
    } else {
      return ProfileActions.api.provideConsent(profile.data.id, policyList);
    }
  }

  isFreshProfile = () => {
    const actionItemViews = this.props.profileActionItems.map(p => p.view);

    // if name, which is a first step, provided then it is no longer a fresh profile
    return _.includes(actionItemViews, ActionItemViewName.profile_name);
  };

  nextStep = (e) => {
    if (e) { e.preventDefault(); }
    const nextStep = this.state.currentStep + 1;
    this.setState({
      currentStep: nextStep,
      isFinished: nextStep >= this.state.steps.length,
    });
  };

  checkValidation = () => {
    const validators = this.state.steps[this.state.currentStep].isValid;

    for (let i = 0; i < validators.length; i++) {
      if (!validators[i]()) {
        this.props.showHintNotification(this.state.steps[this.state.currentStep].viewErrorMessage[i]);
        return false;
      }
    }

    return true;
  }

  submitProfileChanges(e) {
    if (e) { e.preventDefault(); }
    if (!this.checkValidation()) { return; }
    if (this.state.loading) { return; }

    this.consentToPoliciesOnView();

    const profileWithValidValues = { ...this.state.profile.data };
    const genderData = this.props.genderIdMap[profileWithValidValues.gender_id] || {};
    if (!GenderModel.isOther(genderData)) {
      profileWithValidValues.other_gender = '';
    }

    this.setState({ loading: true });

    ProfileActions.api.updateProfile(profileWithValidValues, (apiError) => {
      if (apiError) {
        this.setState({ loading: false });

        if (apiError.isUnavailableForLegalReasons()) {
          this.props.showSystemModal(<DoNotConsentModal withdrawName={WithdrawName.tooYoungDuringSetup} policyName={PolicyName.DATE_OF_BIRTH} isShared={false} />);
        } else {
          this.props.showNotification(apiError.messageKey());
        }
      } else {
        this.props.requestActiveProfile((apiError2, profile) => {
          const nextStep = this.state.currentStep + 1;

          if (nextStep >= this.state.steps.length) {
            this.exitWizard();
            return;
          }

          this.setState({
            profile: new ProfileModel(profile.data),
            prevProfile: new ProfileModel(profile.data),
            currentStep: nextStep,
            loading: false,
          });
        });
      }
    });
  }

  decrementStep() {
    this.setState({ currentStep: this.state.currentStep - 1 });
  }

  stepIsValid() {
    const validators = this.state.steps[this.state.currentStep].isValid;

    for (let i = 0; i < validators.length; i++) {
      if (!validators[i]()) {
        return false;
      }
    }

    return true;
  }

  exitWizard() {
    const userId = this.props.user.data.id;
    const profileId = this.props.profile.data.id;

    ProfileActions.dispatch.setActiveProfile(this.props.profile.data);
    this.props.requestConsents(profileId);
    this.props.refreshActionItems(userId, profileId).then(() => {
      this.setState({
        isFinished: true,
        loading: false,
      });
    });
  }

  handleOnWithdrawPolicyConsent = (policyName) => {
    let name = '';

    if (this.props.isMainProfile) {
      if (this.props.isNewProfile) {
        name = WithdrawName.duringSetup;
      } else if (policyName === PolicyName.GROUP_CONSENT) {
        name = WithdrawName.afterJoinGroup;
      } else {
        name = WithdrawName.afterSetup;
      }
    } else if (policyName === PolicyName.GROUP_CONSENT) {
      name = WithdrawName.afterDependentJoinGroup;
    } else if (this.props.hasSoleAccess) {
      name = WithdrawName.afterDependentSetupSoleAccess;
    } else {
      name = WithdrawName.afterDependentSetupSharedAccess;
    }
    this.props.showSystemModal(<DoNotConsentModal withdrawName={name} policyName={policyName} isShared={!this.props.hasSoleAccess} hasCancelButton={true} />);
  }

  render() {
    if (this.state.isFinished) {
      return <Redirect to={this.props.finishUrl} />;
    }

    const stepProps = this.state.steps[this.state.currentStep];
    const title = stepProps.title ? stepProps.title() : stepProps.phraseData.title;
    const actionItem = this.state.actionItems.filter(a => a.view === stepProps.name)[0];

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            submitDisabled={this.state.loading || !this.stepIsValid() || stepProps.submitDisabled}
            cancel={stepProps.cancel}
            goBack={stepProps.goBack}
            next={stepProps.next || this.submitProfileChanges}
            color="white"
            onDisabledClick={stepProps.onDisabledClick}
            stepTotal={this.state.steps.length - 1}
            currentStep={this.state.currentStep}
          />

          <ContentFrame>
            {
              this.state.profileIsLoaded ? (
                <stepProps.content
                  title={title}
                  profileData={this.state.profile.data}
                  submitForm={this.submitProfileChanges}
                  onChange={this.onChangeProfile}
                  submitErrorText={this.state.submitErrorText}
                  fieldErrorName={this.state.fieldErrorName}
                  clearViewErrors={this.props.dismissNotification}
                  clearFieldErrors={this.clearFieldErrors}
                  defaultOtherGenderText={this.state.defaultOtherGenderText}
                  initialBirthSexId={this.props.profile.getBirthSexId()}
                  phraseData={stepProps.phraseData}
                  isInitialSetup={true}
                  allowWithdraw={this.props.isMainProfile || !this.props.profile.hasUser}
                  profileActionItems={this.props.profileActionItems}
                  actionItem={actionItem}
                  userActionItems={this.props.userActionItems}
                  isFreshProfile={this.isFreshProfile()}
                  isNewProfile={this.isFreshProfile()}
                  showSystemModal={this.props.showSystemModal}
                  onAccept={stepProps.next || this.submitProfileChanges}
                  onWithdrawConsent={stepProps.onWithdrawConsent}
                />
              ) : null
            }
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }
}
ProfileSetupController.propTypes = {
  intl: PropTypes.object.isRequired,
  user: PropTypes.instanceOf(UserModel).isRequired,
  profile: PropTypes.instanceOf(ProfileModel).isRequired,
  submitProfileChanges: PropTypes.func.isRequired,
  requestActiveProfile: PropTypes.func.isRequired,
  genderIdMap: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  showHintNotification: PropTypes.func,
  dismissNotification: PropTypes.func,
  clearIntent: PropTypes.func,
  refreshActionItems: PropTypes.func,
  showSystemModal: PropTypes.func,
  profileActionItems: PropTypes.any,
  userActionItems: PropTypes.any,
  isMainProfile: PropTypes.bool,
  isNewProfile: PropTypes.bool,
  hasSoleAccess: PropTypes.bool,
  goToHome: PropTypes.func,
  switchToMainProfile: PropTypes.func,
  isResidenceStateRequired: PropTypes.func.isRequired,
  requestConsents: PropTypes.func,
  finishUrl: PropTypes.string,
  groupIds: PropTypes.array,
  profileId: PropTypes.number,
};

export default ProfileSetupController;
